import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAppSelector } from "../../../redux/hooks";
import { apiClient } from "../../../zodios/client";
import { getOrganizationsOptions } from "../../../modules/admin/api/useOrganizations";
import type { Organization } from "../../../zodios/common/orgSchema";
import type {
  AmazonAdsClient,
  AmazonProfileSelection,
  RemoteAmazonProfile,
} from "../../../zodios/apiDefinitions/api/profiles";

/** An Amazon Ads profile, one row per seller (or vendor entity) across its marketplaces. */
export interface AmazonProfile {
  profile_id: number;
  entity_id: string;
  profile_name: string;
  profile_type: string;
  is_kdp: boolean;
  marketplaces: string[];
  valid_payment_method: boolean | undefined;
}

/** A connected Amazon Ads login and its profiles. */
export interface AmazonAdsClientProfiles extends Omit<AmazonAdsClient, "profiles"> {
  profiles: AmazonProfile[];
}

/** Groups profiles as the backend does: sellers by name, vendors by entity (vendors can share a name). */
const groupKey = (type: string, name: string, entityId: string) =>
  type === "vendor" ? `${name}-${type}-${entityId}` : `${name}-${type}`;

/** The group `toAmazonProfiles` put this profile in. */
export const profileKey = (profile: AmazonProfile) =>
  groupKey(profile.profile_type, profile.profile_name, profile.entity_id);

/**
 * Amazon returns one profile per marketplace. Merges them into one per seller, and per vendor entity.
 * A group takes its ids from its first profile with a valid payment method, the one the backend imports
 * it through, and lists that profile's marketplace first.
 */
export const toAmazonProfiles = (profiles: RemoteAmazonProfile[] | undefined): AmazonProfile[] => {
  const groups = new Map<string, AmazonProfile>();

  for (const { profileId, countryCode, accountInfo } of profiles ?? []) {
    const { id, type, name, validPaymentMethod } = accountInfo;
    const key = groupKey(type, name, id);
    const group = groups.get(key);

    if (!group) {
      groups.set(key, {
        profile_id: profileId,
        entity_id: id,
        profile_name: name,
        profile_type: type,
        is_kdp: accountInfo.subType === "KDP_AUTHOR",
        marketplaces: [countryCode],
        valid_payment_method: validPaymentMethod,
      });
    } else if (!group.valid_payment_method && validPaymentMethod) {
      group.profile_id = profileId;
      group.entity_id = id;
      group.marketplaces.unshift(countryCode);
      group.valid_payment_method = true;
    } else {
      group.marketplaces.push(countryCode);
    }
  }

  return [...groups.values()];
};

/** How `importAmazonProfiles` identifies a profile. Only sellers and vendors can be imported. */
export const toProfileSelections = (profiles: AmazonProfile[]): AmazonProfileSelection[] =>
  profiles.map((profile) =>
    profile.profile_type === "vendor" ?
      { type: "vendor", name: profile.profile_name, profile_id: profile.profile_id }
    : { type: "seller", name: profile.profile_name },
  );

const MARKETPLACE_ALIASES: Record<string, string> = { US: "USA", GB: "UK" };

const normalizeMarketplace = (code: string) => MARKETPLACE_ALIASES[code.toUpperCase()] ?? code.toUpperCase();

/** Sellers match an organization by name, vendors by name and marketplace. */
const importedKey = (type: string, name: string, marketplace?: string) => JSON.stringify([type, name, marketplace]);

/**
 * Whether a profile is a seller or vendor that Trellis has no organization for yet. KDP authors never
 * are: the import skips them, bar a couple of allowlisted accounts. Undefined until the organizations load.
 */
const useCanImport = (trellisProfiles: Organization[] | undefined) =>
  useMemo(() => {
    if (!trellisProfiles) {
      return undefined;
    }
    const imported = new Set(
      trellisProfiles.flatMap((org) =>
        org.profile_type === "seller" ? [importedKey("seller", org.profile_name)]
        : org.profile_type === "vendor" ?
          org.marketplaces.map((marketplace) => importedKey("vendor", org.profile_name, marketplace))
        : [],
      ),
    );

    return (profile: AmazonProfile) =>
      !profile.is_kdp &&
      (profile.profile_type === "seller" ?
        !imported.has(importedKey("seller", profile.profile_name))
      : profile.profile_type === "vendor" &&
        !imported.has(
          importedKey("vendor", profile.profile_name, normalizeMarketplace(profile.marketplaces[0] ?? "")),
        ));
  }, [trellisProfiles]);

/**
 * Groups every login's profiles together, as the import does, and lists each group under the first login
 * that reaches it.
 */
const dedupeClientProfiles = (clients: AmazonAdsClient[]) => {
  const firstClient = new Map<string, number>();
  const rawProfiles = new Map<number, RemoteAmazonProfile>();
  for (const client of clients) {
    for (const profile of client.profiles) {
      const { id, type, name } = profile.accountInfo;
      const key = groupKey(type, name, id);
      if (!firstClient.has(key)) {
        firstClient.set(key, client.client_id);
      }
      rawProfiles.set(profile.profileId, profile);
    }
  }

  const profiles = toAmazonProfiles([...rawProfiles.values()]);
  return clients.map((client): AmazonAdsClientProfiles => ({
    ...client,
    profiles: profiles.filter((profile) => firstClient.get(profileKey(profile)) === client.client_id),
  }));
};

/** Profiles of the current org's Ads credential. The backend can drop those another account already owns. */
const useRemoteProfilesQuery = (excludeOwnedElsewhere: boolean, enabled = true) => {
  const orgId = useAppSelector((state) => state.user.organization_id);
  return useQuery({
    queryKey: [{ entity: "remoteAmazonProfiles", orgId, excludeOwnedElsewhere }],
    queryFn: async () =>
      await apiClient.getRemoteAmazonProfiles({
        queries: { filter_out_duplicates: excludeOwnedElsewhere },
      }),
    select: toAmazonProfiles,
    enabled: enabled && !!orgId,
  });
};

/** Every profile Amazon returns for the current org's Ads credential. Calls the Amazon Ads API. */
export const useRemoteAmazonProfiles = () => useRemoteProfilesQuery(false);

/**
 * Each Amazon Ads login connected to the account, with the profiles it reaches that no other Trellis
 * account owns. A profile reachable from several logins is listed under the first one only. Calls the
 * Amazon Ads API once per login.
 */
export const useAmazonAdsClients = () => {
  const orgId = useAppSelector((state) => state.user.organization_id);
  return useQuery({
    queryKey: [{ entity: "amazonAdsClients", orgId }],
    queryFn: async () => await apiClient.getAmazonAdsClients({ queries: { org_id: orgId } }),
    select: dedupeClientProfiles,
    enabled: !!orgId,
  });
};

/**
 * Profiles of the current org's Ads credential that it can import: seller and vendor profiles with a
 * valid payment method that no Trellis account owns yet, KDP authors excluded. `data` is undefined
 * while loading.
 */
export const useImportableAmazonProfiles = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const trellisProfilesQuery = useQuery(getOrganizationsOptions({ channel: "AMAZON" }));
  const remoteProfilesQuery = useRemoteProfilesQuery(true, enabled);
  const canImport = useCanImport(trellisProfilesQuery.data);

  const remoteProfiles = remoteProfilesQuery.data;
  const data = useMemo(
    () => canImport && remoteProfiles?.filter((profile) => canImport(profile) && profile.valid_payment_method),
    [canImport, remoteProfiles],
  );

  return { data, error: trellisProfilesQuery.error ?? remoteProfilesQuery.error };
};

/**
 * Each connected Amazon Ads login with the seller and vendor profiles no Trellis account owns yet,
 * whatever their payment method, KDP authors excluded. `data` is undefined while loading.
 */
export const useImportableAmazonProfilesByClient = () => {
  const trellisProfilesQuery = useQuery(getOrganizationsOptions({ channel: "AMAZON" }));
  const clientsQuery = useAmazonAdsClients();
  const canImport = useCanImport(trellisProfilesQuery.data);

  const clients = clientsQuery.data;
  const data = useMemo(
    () =>
      canImport &&
      clients?.map((client) => ({ ...client, profiles: client.profiles.filter((profile) => canImport(profile)) })),
    [canImport, clients],
  );

  return { data, error: trellisProfilesQuery.error ?? clientsQuery.error };
};
